import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { CORE_PAGE_SEO } from '../data/corePageSeo';
import { CUSTOMER_SUPPORT } from '../data/customerPolicies';
import { DRIVER_SUPPORT } from '../data/driverAppSupport';
import { buildContactWhatsAppUrl, validateContactForm } from '../lib/contactWhatsApp';
import '../styles/ContactPage.css';

const MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=Easy%20Drop%20Taxi%20Coimbatore';
const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.5454639536047!2d77.01521007480831!3d10.997640789164924!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba857892d511259%3A0x3cdfcefc3720c6a5!2sEasy%20Drop%20Taxi!5e0!3m2!1sen!2sin!4v1766329153131!5m2!1sen!2sin';

const EMPTY_FORM = {
  name: '',
  mobile: '',
  email: '',
  subject: '',
  message: '',
};

const Contact = () => {
  const seo = CORE_PAGE_SEO.contact;
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const phoneTel = CUSTOMER_SUPPORT.phoneTel.replace('tel:', '');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact EasyDropTaxi',
    url: 'https://easydroptaxi.com/contact',
    description: seo.description,
    mainEntity: {
      '@type': 'Organization',
      name: 'Easy Drop Taxi',
      url: 'https://easydroptaxi.com/',
      telephone: phoneTel,
      email: CUSTOMER_SUPPORT.email,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Coimbatore',
        addressRegion: 'Tamil Nadu',
        addressCountry: 'IN',
      },
      areaServed: ['Tamil Nadu', 'Puducherry', 'Kerala', 'Karnataka', 'Andhra Pradesh'],
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const result = validateContactForm(formData);
    setErrors(result.errors);
    if (!result.ok) return;

    const url = buildContactWhatsAppUrl(DRIVER_SUPPORT.whatsappNumber, result.values);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <main className="contact-page">
      <SeoHead
        title={seo.title}
        description={seo.description}
        path={seo.path}
        jsonLd={jsonLd}
      />

      <section className="contact-hero" aria-labelledby="contact-heading">
        <div className="container">
          <h1 id="contact-heading">Contact EasyDropTaxi</h1>
          <p>Need a taxi or have a question? We're here to help.</p>
        </div>
      </section>

      <section className="contact-body" aria-labelledby="contact-details-heading">
        <div className="container">
          <h2 id="contact-details-heading" className="contact-section-title">Talk to our team</h2>
          <p className="contact-intro">
            EasyDropTaxi is headquartered in Coimbatore, Tamil Nadu, and helps customers book one-way drop taxis, round trips, airport transfers, and outstation taxi services. Support covers travel across Tamil Nadu, Puducherry, Kerala, Karnataka, and Andhra Pradesh.
          </p>

          <div className="contact-info-grid">
            <article className="contact-info-card contact-info-card--address">
              <i className="fa fa-map-marker" aria-hidden="true"></i>
              <div>
                <h3>Address</h3>
                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer">
                  {DRIVER_SUPPORT.address}
                </a>
              </div>
            </article>
            <article className="contact-info-card">
              <i className="fa fa-phone" aria-hidden="true"></i>
              <div>
                <h3>Phone</h3>
                <a href={CUSTOMER_SUPPORT.phoneTel}>{CUSTOMER_SUPPORT.phoneDisplay}</a>
              </div>
            </article>
            <article className="contact-info-card">
              <i className="fa fa-envelope" aria-hidden="true"></i>
              <div>
                <h3>Email</h3>
                <a href={CUSTOMER_SUPPORT.mailto}>{CUSTOMER_SUPPORT.email}</a>
              </div>
            </article>
          </div>

          <div className="contact-form-wrap">
            <h2 className="contact-section-title">Send us a message</h2>
            <p className="contact-intro">
              Send your question on WhatsApp. You can review it before it is delivered. For a fare, use the <Link to="/#home-booking">booking form</Link> or browse <Link to="/services">taxi services</Link> and <Link to="/taxi-routes">routes</Link>.
            </p>
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="contact-form-grid">
                <div className="contact-field">
                  <label htmlFor="contact-name">Name <span aria-hidden="true">*</span></label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    aria-required="true"
                    aria-invalid={errors.name ? 'true' : 'false'}
                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  />
                  {errors.name ? <p id="contact-name-error" className="contact-error" role="alert">{errors.name}</p> : null}
                </div>
                <div className="contact-field">
                  <label htmlFor="contact-mobile">Mobile number <span aria-hidden="true">*</span></label>
                  <input
                    id="contact-mobile"
                    type="tel"
                    name="mobile"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="10-digit mobile number"
                    value={formData.mobile}
                    onChange={handleChange}
                    aria-required="true"
                    aria-invalid={errors.mobile ? 'true' : 'false'}
                    aria-describedby={errors.mobile ? 'contact-mobile-error' : undefined}
                  />
                  {errors.mobile ? <p id="contact-mobile-error" className="contact-error" role="alert">{errors.mobile}</p> : null}
                </div>
              </div>

              <div className="contact-field">
                <label htmlFor="contact-email">Email <span className="contact-optional">(optional)</span></label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  aria-required="false"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'contact-email-error' : undefined}
                />
                {errors.email ? <p id="contact-email-error" className="contact-error" role="alert">{errors.email}</p> : null}
              </div>

              <div className="contact-field">
                <label htmlFor="contact-subject">Subject <span aria-hidden="true">*</span></label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  aria-required="true"
                  aria-invalid={errors.subject ? 'true' : 'false'}
                  aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                />
                {errors.subject ? <p id="contact-subject-error" className="contact-error" role="alert">{errors.subject}</p> : null}
              </div>

              <div className="contact-field">
                <label htmlFor="contact-message">Message <span aria-hidden="true">*</span></label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  aria-required="true"
                  aria-invalid={errors.message ? 'true' : 'false'}
                  aria-describedby={errors.message ? 'contact-message-error' : undefined}
                />
                {errors.message ? <p id="contact-message-error" className="contact-error" role="alert">{errors.message}</p> : null}
              </div>

              <button type="submit" className="contact-submit">Send Message on WhatsApp</button>
            </form>
          </div>

          <p className="contact-hours">
            Customer support is available 24 hours a day, including weekends, for booking help and travel questions.
          </p>
        </div>
      </section>

      <section className="contact-map" aria-labelledby="contact-map-heading">
        <div className="container">
          <h2 id="contact-map-heading" className="contact-section-title">Find us</h2>
          <div className="contact-map-frame">
            <iframe
              title="Easy Drop Taxi location in Coimbatore"
              src={MAP_EMBED}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
